import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { SECTION_COLORS } from "../../constants";

const JsonBlock = ({ label, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    const isEmpty = typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0;
    if (isEmpty) return null;
    return (
        <Box mt={1.5} p={1.5} sx={{ borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
            <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.segments.main} mb={0.5}>{label}</Typography>
            <Typography component="pre" fontSize={11}
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                {JSON.stringify(data, null, 2)}
            </Typography>
        </Box>
    );
};

const ViewSegmentDialog = ({ open, segment, onClose, onEditRequest, onManageMembers }) => {
    if (!segment) return null;
    const rows = [
        { label: "Nom", value: segment.name || "–" },
        { label: "Description", value: segment.description || "–" },
        { label: "Type", value: segment.is_dynamic ? "Dynamique" : "Statique" },
        { label: "Marque", value: segment.brand },
        { label: "Dernier calcul", value: segment.last_computed_at ? new Date(segment.last_computed_at).toLocaleString("fr-FR") : "–" },
        { label: "Créé le", value: segment.created_at ? new Date(segment.created_at).toLocaleDateString("fr-FR") : "–" },
        { label: "Identifiant", value: segment.id },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {segment.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail du segment</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: segment.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${segment.active ? "#86efac" : "#fca5a5"}`,
                    }}>
                    {segment.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={segment.active ? "#16a34a" : "#DC2626"}>
                        {segment.active ? "Actif" : "Inactif"}
                    </Typography>
                    <Chip label={segment.is_dynamic ? "Dynamique" : "Statique"} size="small"
                        sx={{
                            ml: "auto", fontWeight: 700, fontSize: 11,
                            bgcolor: SECTION_COLORS.segments.light, color: SECTION_COLORS.segments.main,
                        }} />
                </Box>

                <Stack spacing={1} mb={1}>
                    {rows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 150 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                        </Box>
                    ))}
                </Stack>

                {segment.is_dynamic && <JsonBlock label="conditions" data={segment.conditions} />}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                {!segment.is_dynamic && onManageMembers && (
                    <Button
                        variant="outlined"
                        onClick={() => { onClose(); onManageMembers(segment); }}
                        startIcon={<PeopleAltIcon />}
                        sx={{
                            borderColor: SECTION_COLORS.segments.main,
                            color: SECTION_COLORS.segments.main,
                            textTransform: "none", borderRadius: 2, fontWeight: 600, px: 2,
                            "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}10` },
                        }}
                    >
                        Gérer les membres
                    </Button>
                )}
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(segment); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.segments.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewSegmentDialog;
